import { BRANCH_PROFILE_ASSET } from 'constants/enums/asset';
import { useUpdateBranchProfileMutation } from 'api/branches/query';
import { useCurrentBranch } from './useCurrentBranch';

const TARGET_RATIO = 16 / 9;
const TARGET_WIDTH = 1280;
const TARGET_HEIGHT = 720;

export const useBranchCoverImage = () => {
  const { clientStates } = useCurrentBranch();
  const { mutateAsync, isPending: isLoading } = useUpdateBranchProfileMutation(clientStates.branches ?? '');

  const loadImage = async (src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(src);
        resolve(img);
      };
      img.onerror = reject;
      img.src = src;
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file?.type.startsWith('image/')) return;
    const tempUrl = URL.createObjectURL(file);

    const img = await loadImage(tempUrl);
    const { width: w, height: h } = img;

    let cropW = w,
      cropH = w / TARGET_RATIO;
    if (cropH > h) {
      cropH = h;
      cropW = h * TARGET_RATIO;
    }
    const x = (w - cropW) / 2;
    const y = (h - cropH) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = TARGET_WIDTH;
    canvas.height = TARGET_HEIGHT;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, x, y, cropW, cropH, 0, 0, TARGET_WIDTH, TARGET_HEIGHT);

    const blob: Blob = await new Promise(res => canvas.toBlob(b => res(b as Blob), 'image/jpeg', 0.9));
    const baseName = (file.name || 'cover').replace(/\.[^/.]+$/, '');
    const croppedFile = new File([blob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });

    await mutateAsync({ file: croppedFile, type: BRANCH_PROFILE_ASSET.COVER_IMAGE });
    event.target.value = '';
  };

  return {
    actions: {
      handleFileSelect,
    },
    apiStates: {
      isLoading,
    },
  };
};
